import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import { effectiveTenantId, useAuthStore, type SessionUser } from './authStore';
import { hasAccess, isPlatformRole, type AccessRequirement } from './roles';

export interface LoginInput {
  email: string;
  password: string;
  totp?: string;
}

/** Session state + actions. Components should use this instead of touching the store directly. */
export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const queryClient = useQueryClient();

  const login = useCallback(async (input: LoginInput) => {
    const tokens = unwrap(await api.POST('/v1/auth/login', { body: { ...input, totp: input.totp ?? null } }));
    useAuthStore.getState().setSession(tokens);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (refreshToken) {
      try {
        await api.POST('/v1/auth/logout', { body: { refreshToken } });
      } catch {
        /* logout is best effort */
      }
    }
    useAuthStore.getState().clear();
    queryClient.clear();
  }, [queryClient]);

  return { user, isAuthenticated: accessToken !== null && user !== null, login, logout };
}

/** Checks role/cap requirements against the current session. */
export function useCan(req: AccessRequirement): boolean {
  const user = useAuthStore((s) => s.user);
  return user !== null && hasAccess(user.roles, user.caps, req);
}

export function useIsPlatformUser(): boolean {
  const user = useAuthStore((s) => s.user);
  return user !== null && isPlatformRole(user.roles);
}

/** The tenant id that API calls are scoped to (null = platform-global). Include it in query keys. */
export function useTenantId(): string | null {
  return useAuthStore((s) => effectiveTenantId(s));
}

export type BootstrapState = 'pending' | 'ready';

/**
 * Single-flight bootstrap refresh. React StrictMode (dev) runs effects twice; two concurrent refreshes with the same
 * rotating refresh token trip the API's reuse detection and revoke the session, so the exchange is shared.
 */
let bootstrapRefresh: Promise<void> | null = null;

export function refreshSessionOnce(): Promise<void> {
  bootstrapRefresh ??= (async () => {
    const refreshToken = useAuthStore.getState().refreshToken;
    try {
      const res = await api.POST('/v1/auth/refresh', { body: { refreshToken: refreshToken ?? '' } });
      if (res.data) useAuthStore.getState().setSession(res.data);
      else useAuthStore.getState().clear();
    } catch {
      useAuthStore.getState().clear();
    } finally {
      bootstrapRefresh = null;
    }
  })();
  return bootstrapRefresh;
}

/**
 * On first load the access token is gone (memory only); if a refresh token exists, exchange it once.
 */
export function useSessionBootstrap(): BootstrapState {
  const [state, setState] = useState<BootstrapState>(() =>
    useAuthStore.getState().refreshToken && !useAuthStore.getState().accessToken ? 'pending' : 'ready',
  );

  useEffect(() => {
    if (state !== 'pending') return;
    let cancelled = false;
    void refreshSessionOnce().then(() => {
      if (!cancelled) setState('ready');
    });
    return () => {
      cancelled = true;
    };
  }, [state]);

  return state;
}

export type { SessionUser };
